import React, { useState } from "react";
import { GET_IPHONE } from "../queries/iPhoneQueries";
import DeviceDetailBase from "../components/DeviceDetailBase";
import {
  updateDeviceInventory,
  restartDevice,
  clearPasscode,
} from "../commands/mdmCommands.js";
import InstallProfileModal from '../components/modals/InstallProfileModal.jsx';
import EraseDeviceModal from '../components/modals/EraseDeviceModal.jsx';
import { calculateStoragePercentage, formatMacAddress } from '../components/DeviceDetailBase';
import LockDeviceModal from "../components/modals/LockDeviceModal.jsx";
import LostModeModal from "../components/modals/LostModeModal.jsx";
import ActivationLockBypassModal from "../components/modals/ActivationLockBypassModal.jsx";
import TimeZoneModal from "../components/modals/TimeZoneModal.jsx";
import BluetoothModal from "../components/modals/BluetoothModal.jsx";
import ShutdownDeviceModal from "../components/modals/ShutdownDeviceModal.jsx";

function mapsUrl(location) {
  if (location?.latitude == null || location?.longitude == null) return null;
  return `https://www.google.com/maps?q=${location.latitude},${location.longitude}`;
}

export default function IPhoneDetail() {
  const [showInstallProfileModal, setShowInstallProfileModal] = useState(false);
  const [showEraseDeviceModal, setShowEraseDeviceModal] = useState(false);
  const [showLockDeviceModal, setShowLockDeviceModal] = useState(false);
  const [showLostModeModal, setShowLostModeModal] = useState(false);
  const [showBypassModal, setShowBypassModal] = useState(false);
  const [showTimeZoneModal, setShowTimeZoneModal] = useState(false);
  const [showBluetoothModal, setShowBluetoothModal] = useState(false);
  const [showShutdownDeviceModal, setShowShutdownDeviceModal] = useState(false);
  const actionButtons = [
    {
      label: "Clear Passcode",
      capability: "canClearPasscode",
      onClick: (device) => clearPasscode(device.UDID, "iOS"),
    },
    {
      label: "Erase Device",
      capability: "canErase",
      onClick: () => setShowEraseDeviceModal(true),
    },
    {
      label: "Install Profile",
      capability: "canInstallProfile",
      onClick: () => setShowInstallProfileModal(true),
    },
    {
      label: "Lock Device",
      capability: "canLock",
      onClick: () => setShowLockDeviceModal(true),
    },
    {
      label: "Lost Mode",
      capability: "canLostMode",
      requiresSupervised: true,
      hideForUserEnrollment: true,
      getLabel: (device) => device.lostMode?.enabled ? "Lost Mode (aktiv)" : "Lost Mode",
      onClick: () => setShowLostModeModal(true),
    },
    {
      label: "Activation-Lock-Bypass",
      capability: "canActivationLockBypass",
      hideForUserEnrollment: true,
      onClick: () => setShowBypassModal(true),
    },
    {
      label: "Zeitzone",
      capability: "canTimeZone",
      hideForUserEnrollment: true,
      onClick: () => setShowTimeZoneModal(true),
    },
    {
      label: "Bluetooth",
      capability: "canBluetooth",
      requiresSupervised: true,
      hideForUserEnrollment: true,
      onClick: () => setShowBluetoothModal(true),
    },
    {
      label: "Restart Device",
      capability: "canRestart",
      onClick: (device) => restartDevice(device.UDID),
    },
    {
      label: "Herunterfahren",
      capability: "canShutdown",
      requiresSupervised: true,
      hideForUserEnrollment: true,
      onClick: () => setShowShutdownDeviceModal(true),
    },
  ];

  const infoSections = [
    {
      title: 'hardware',
      getData: (device) => ({
        "serial number": device.SerialNumber,
        "product name": device.QueryResponses?.ProductName,
        "model number": device.QueryResponses?.ModelNumber,
        "storage": calculateStoragePercentage(device),
      })
    },
    {
      title: 'network',
      getData: (device) => ({
        "Cellular Network": device.QueryResponses?.ServiceSubscriptions?.[0]?.SubscriberCarrierNetwork,
        "Phone Number": device.QueryResponses?.ServiceSubscriptions?.[0]?.PhoneNumber,
        "IMEI": device.QueryResponses?.ServiceSubscriptions?.[0]?.IMEI,
        "WiFi MAC address": formatMacAddress(device.QueryResponses?.WiFiMAC),
        "bluetooth MAC address": formatMacAddress(device.QueryResponses?.BluetoothMAC),
      })
    },
    {
      title: 'operating system',
      getData: (device) => ({
        "iOS version": device.QueryResponses?.OSVersion,
        "build version": device.QueryResponses?.BuildVersion,
        "MDM profile installed": device.mdmProfileInstalled,
        "supervised": device.QueryResponses?.IsSupervised,
        "enrollment": device.SecurityInfo?.ManagementStatus?.IsUserEnrollment
          ? "User Enrollment"
          : (device.enrollmentContext?.hasDepAssignment ? "ADE / DEP" : "Device Enrollment"),
        "DEP supervised (Soll)": device.enrollmentContext?.depIsSupervised == null
          ? "—"
          : (device.enrollmentContext.depIsSupervised ? "ja" : "nein"),
        "Zeitzone": device.QueryResponses?.TimeZone || "—",
      })
    },
    {
      title: 'sicherheit',
      getData: (device) => {
        const loc = device.lostModeLocation;
        const link = mapsUrl(loc);
        return {
          "Lost Mode": device.lostMode?.enabled ? "aktiv" : "inaktiv",
          "Lost-Mode-Nachricht": device.lostMode?.message || "—",
          "Standort": link ? (
            <a href={link} target="_blank" rel="noreferrer">
              {Number(loc.latitude).toFixed(5)}, {Number(loc.longitude).toFixed(5)}
            </a>
          ) : "—",
          "Activation-Lock-Bypass": device.activationLockBypassCode?.code ? "vorhanden" : "—",
          "Bluetooth": device.bluetoothEnabled === true
            ? "an"
            : device.bluetoothEnabled === false ? "aus" : "—"
        };
      }
    },
  ];

  const renderModals = (device, configProfiles, onCommandIssued) => (
    <>
      {showInstallProfileModal && (
        <InstallProfileModal
          visible={showInstallProfileModal}
          UDID={device.UDID}
          currentProfiles={device.Profiles}
          configProfiles={configProfiles}
          hideInstallProfileModal={() => setShowInstallProfileModal(false)}
        />
      )}
      {showEraseDeviceModal && (
        <EraseDeviceModal
          visible={showEraseDeviceModal}
          UDID={device.UDID}
          platform="ios"
          device={device}
          configProfiles={configProfiles || []}
          hideEraseDeviceModal={() => setShowEraseDeviceModal(false)}
        />
      )}
      {showLockDeviceModal && (
        <LockDeviceModal
          visible={showLockDeviceModal}
          UDID={device.UDID}
          hideLockDeviceModal={() => setShowLockDeviceModal(false)}
        />
      )}
      {showLostModeModal && (
        <LostModeModal
          visible={showLostModeModal}
          device={device}
          hideLostModeModal={() => setShowLostModeModal(false)}
          onIssued={onCommandIssued}
        />
      )}
      {showBypassModal && (
        <ActivationLockBypassModal
          visible={showBypassModal}
          device={device}
          hideModal={() => setShowBypassModal(false)}
          onIssued={onCommandIssued}
        />
      )}
      {showTimeZoneModal && (
        <TimeZoneModal
          visible={showTimeZoneModal}
          device={device}
          hideModal={() => setShowTimeZoneModal(false)}
          onIssued={onCommandIssued}
        />
      )}
      {showBluetoothModal && (
        <BluetoothModal
          visible={showBluetoothModal}
          device={device}
          hideModal={() => setShowBluetoothModal(false)}
          onIssued={onCommandIssued}
        />
      )}
      {showShutdownDeviceModal && (
        <ShutdownDeviceModal
          visible={showShutdownDeviceModal}
          UDID={device.UDID}
          hideShutdownDeviceModal={() => setShowShutdownDeviceModal(false)}
        />
      )}
    </>
  );

  return (
    <DeviceDetailBase
      query={GET_IPHONE}
      deviceType="iphone"
      getDeviceData={(data) => data.iphone}
      actionButtons={actionButtons}
      renderModals={renderModals}
      infoSections={infoSections}
      onRefresh={(device) => updateDeviceInventory("ios", device.UDID)}
    />
  );
}
